import React, { useState } from 'react';
import { createBooking } from '../services/api';

export default function BookingForm({ flight, searchParams, onBookingComplete, onBack, onError }) {
  const [loading, setLoading] = useState(false);
  const numAdults = searchParams?.adults || 1;

  // Initialize traveler forms
  const [travelers, setTravelers] = useState(
    Array.from({ length: numAdults }, (_, i) => ({
      firstName: '',
      lastName: '',
      birthDate: '',
      email: i === 0 ? '' : undefined,
      phone: i === 0 ? '' : undefined,
      type: 'ADT',
    }))
  );

  const updateTraveler = (index, field, value) => {
    setTravelers((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    onError(null);
    setLoading(true);

    try {
      // Build segments from the selected flight
      const segments = [];
      for (const leg of flight.legs) {
        for (const sched of leg.schedules) {
          segments.push({
            flightNumber: sched.carrier?.marketingFlightNumber,
            airlineCode: sched.carrier?.marketing,
            origin: sched.departure?.airport,
            destination: sched.arrival?.airport,
            departureDate: searchParams?.departureDate, // simplified
            departureTime: sched.departure?.time?.substring(0, 5),
            bookingClass: 'Y', // default economy; real impl would use fare class
          });
        }
      }

      const result = await createBooking({
        flightSegments: { segments },
        travelers,
        contactInfo: {
          emails: travelers[0]?.email ? [travelers[0].email] : [],
          phones: travelers[0]?.phone ? [{ number: travelers[0].phone, type: 'AGENCY' }] : [],
        },
      });

      onBookingComplete(result);
    } catch (err) {
      const msg =
        err.response?.data?.details?.message ||
        err.response?.data?.error ||
        err.message ||
        'Booking failed';
      onError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Build a summary of the selected flight
  const flightSummary = flight.legs
    .map((leg) => {
      const first = leg.schedules[0];
      const last = leg.schedules[leg.schedules.length - 1];
      return `${first?.departure?.airport || '?'} ${first?.departure?.time?.substring(0, 5) || ''} → ${last?.arrival?.airport || '?'} ${last?.arrival?.time?.substring(0, 5) || ''} (${leg.schedules.map((s) => `${s.carrier?.marketing}${s.carrier?.marketingFlightNumber}`).join(', ')})`;
    })
    .join(' | ');

  return (
    <div className="booking-panel">
      <button className="btn btn-outline btn-sm" onClick={onBack} style={{ marginBottom: 16 }}>
        &larr; Back to Results
      </button>

      <h2>Complete Your Booking</h2>

      {/* Selected Flight Summary */}
      <div
        style={{
          padding: 16,
          background: '#f0f9ff',
          borderRadius: 8,
          marginBottom: 20,
          border: '1px solid #bae6fd',
        }}
      >
        <div style={{ fontSize: 14, color: '#0369a1', fontWeight: 500 }}>Selected Flight</div>
        <div style={{ fontSize: 13, marginTop: 4 }}>{flightSummary}</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#1a56db', marginTop: 8 }}>
          ${typeof flight.totalPrice === 'number' ? flight.totalPrice.toFixed(2) : flight.totalPrice}{' '}
          {flight.currency}
        </div>
      </div>

      {/* Traveler Details */}
      <form onSubmit={handleSubmit}>
        {travelers.map((t, i) => (
          <div className="traveler-form" key={i}>
            <h3>
              Traveler {i + 1} {i === 0 ? '(Lead)' : ''}
            </h3>
            <div className="form-group">
              <label>First Name</label>
              <input
                type="text"
                value={t.firstName}
                onChange={(e) => updateTraveler(i, 'firstName', e.target.value)}
                required
                placeholder="John"
              />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                value={t.lastName}
                onChange={(e) => updateTraveler(i, 'lastName', e.target.value)}
                required
                placeholder="Doe"
              />
            </div>
            <div className="form-group">
              <label>Date of Birth</label>
              <input
                type="date"
                value={t.birthDate}
                onChange={(e) => updateTraveler(i, 'birthDate', e.target.value)}
                required
              />
            </div>
            {i === 0 && (
              <>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={t.email}
                    onChange={(e) => updateTraveler(i, 'email', e.target.value)}
                    required
                    placeholder="john@example.com"
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={t.phone}
                    onChange={(e) => updateTraveler(i, 'phone', e.target.value)}
                    required
                    placeholder="+1234567890"
                  />
                </div>
              </>
            )}
          </div>
        ))}

        <div className="booking-actions">
          <button className="btn btn-primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }}></span>
                Creating Booking...
              </>
            ) : (
              'Confirm Booking'
            )}
          </button>
          <button className="btn btn-outline" type="button" onClick={onBack}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
